<script lang="ts">
	import { page } from '$app/stores';
	import { cn } from '$lib/utils';
	import {
		Calendar as CalendarIcon,
		Radio as RadioIcon,
		BookOpen,
		ChevronDown,
		Home,
		Info,
		Users,
		MessageCircle,
		Search,
		Mail,
		Rss
	} from 'lucide-svelte';
	import clickOutside from '$lib/actions/clickOutside';

	export let mobileMenuOpen = false;
	export let mobileMembershipOpen = false;
	export let mobileResourcesOpen = false;
	export let mobileNetsOpen = false;
	export let mobileCalendarOpen = false;
</script>

{#if mobileMenuOpen}
	<div class="md:hidden">
		<div class="space-y-1 px-2 pt-2 pb-3">
			<a
				href="/"
				class={cn(
					'flex items-center rounded-md px-3 py-2 text-base font-medium',
					$page.url.pathname === '/'
						? 'bg-blue-900 dark:bg-blue-800'
						: 'hover:bg-blue-700 dark:hover:bg-blue-800'
				)}
			>
				<Home class="mr-2 h-4 w-4" />
				Home</a
			>
			<a
				href="/about"
				class={cn(
					'flex items-center rounded-md px-3 py-2 text-base font-medium',
					$page.url.pathname === '/about'
						? 'bg-blue-900 dark:bg-blue-800'
						: 'hover:bg-blue-700 dark:hover:bg-blue-800'
				)}
			>
				<Info class="mr-2 h-4 w-4" />
				About</a
			>

			<!-- Mobile Membership dropdown -->
			<div class="relative" use:clickOutside on:outclick={() => (mobileMembershipOpen = false)}>
				<button
					id="mobile-membership-button"
					class={cn(
						'flex w-full items-center rounded-md px-3 py-2 text-base font-medium',
						$page.url.pathname.startsWith('/membership') ||
							$page.url.pathname.startsWith('/meetings')
							? 'bg-blue-900 dark:bg-blue-800'
							: 'hover:bg-blue-700 dark:hover:bg-blue-800'
					)}
					on:click={() => (mobileMembershipOpen = !mobileMembershipOpen)}
				>
					<Users class="mr-2 h-4 w-4" />
					Membership
					<span class="transition-transform" class:rotate-180={mobileMembershipOpen}>
						<ChevronDown class="ml-1 h-4 w-4" aria-hidden="true" />
					</span>
				</button>
				{#if mobileMembershipOpen}
					<div
						id="mobile-membership-dropdown"
						class="mt-2 space-y-1 rounded-md bg-blue-700 px-4 py-2"
					>
						<a
							href="/membership"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/membership'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileMembershipOpen = false;
								mobileMenuOpen = false;
							}}
						>
							Info/Join/Renew
						</a>
						<a
							href="/meetings"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname.startsWith('/meetings')
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileMembershipOpen = false;
								mobileMenuOpen = false;
							}}
						>
							Meeting Minutes
						</a>

						<div class="pt-2">
							<p class="px-3 py-1 text-xs font-semibold text-blue-200">Club Documents</p>
							<a
								href="/membership/constitution"
								class={cn(
									'block rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/membership/constitution'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileMembershipOpen = false;
									mobileMenuOpen = false;
								}}
							>
								Constitution
							</a>
							<a
								href="/membership/bylaws"
								class={cn(
									'block rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/membership/bylaws'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileMembershipOpen = false;
									mobileMenuOpen = false;
								}}
							>
								Club Bylaws
							</a>
						</div>

						<div class="pt-2">
							<p class="px-3 py-1 text-xs font-semibold text-blue-200">Corporate Documents</p>
							<a
								href="/membership/dsarc-inc/bylaws"
								class={cn(
									'block rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/membership/dsarc-inc/bylaws'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileMembershipOpen = false;
									mobileMenuOpen = false;
								}}
							>
								Corporate Bylaws
							</a>
						</div>

						<div class="pt-2">
							<p class="px-3 py-1 text-xs font-semibold text-blue-200">AUXCOMM</p>
							<a
								href="/membership/auxcomm/bylaws"
								class={cn(
									'block rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/membership/auxcomm/bylaws'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileMembershipOpen = false;
									mobileMenuOpen = false;
								}}
							>
								AUXCOMM Bylaws
							</a>
						</div>
					</div>
				{/if}
			</div>

			<!-- Mobile Nets dropdown -->
			<div class="relative" use:clickOutside on:outclick={() => (mobileNetsOpen = false)}>
				<button
					type="button"
					id="mobile-nets-button"
					class={cn(
						'flex w-full items-center rounded-md px-3 py-2 text-base font-medium',
						$page.url.pathname.startsWith('/nets')
							? 'bg-blue-900 dark:bg-blue-800'
							: 'hover:bg-blue-700 dark:hover:bg-blue-800'
					)}
					on:click={() => (mobileNetsOpen = !mobileNetsOpen)}
					aria-expanded={mobileNetsOpen}
				>
					<RadioIcon class="mr-2 h-4 w-4" />
					<span>Nets</span>
					<ChevronDown
						class={cn('ml-1 h-5 w-5 transition-transform', mobileNetsOpen && 'rotate-180')}
					/>
				</button>

				{#if mobileNetsOpen}
					<div
						id="mobile-nets-dropdown"
						class="mt-2 space-y-1 rounded-md bg-blue-700 px-4 py-2"
						role="menu"
						aria-orientation="vertical"
						aria-labelledby="mobile-nets-button"
						tabindex="-1"
					>
						<a
							href="/nets/packet"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/nets/packet'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							role="menuitem"
							tabindex="-1"
							on:click={() => {
								mobileNetsOpen = false;
								mobileMenuOpen = false;
							}}
						>
							Packet Radio
						</a>
						<a
							href="/nets/auxcomm_ares"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/nets/auxcomm_ares'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							role="menuitem"
							tabindex="-1"
							on:click={() => {
								mobileNetsOpen = false;
								mobileMenuOpen = false;
							}}
						>
							AUXCOMM/ARES
						</a>
					</div>
				{/if}
			</div>

			<!-- Mobile Events dropdown -->
			<div class="relative" use:clickOutside on:outclick={() => (mobileCalendarOpen = false)}>
				<button
					id="mobile-calendar-button"
					class={cn(
						'flex w-full items-center rounded-md px-3 py-2 text-base font-medium',
						$page.url.pathname.startsWith('/calendar') ||
							$page.url.pathname.startsWith('/events') ||
							$page.url.pathname === '/newsletter'
							? 'bg-blue-900 dark:bg-blue-800'
							: 'hover:bg-blue-700 dark:hover:bg-blue-800'
					)}
					on:click={() => (mobileCalendarOpen = !mobileCalendarOpen)}
				>
					<CalendarIcon class="mr-2 h-4 w-4" />
					Events
					<span class="transition-transform" class:rotate-180={mobileCalendarOpen}>
						<ChevronDown class="ml-1 h-4 w-4" aria-hidden="true" />
					</span>
				</button>

				{#if mobileCalendarOpen}
					<div class="mt-2 space-y-1 rounded-md bg-blue-700 px-4 py-2">
						<a
							href="/calendar"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/calendar'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileCalendarOpen = false;
								mobileMenuOpen = false;
							}}
						>
							Calendar
						</a>
						<a
							href="/events"
							class={cn(
								'block rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/events'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileCalendarOpen = false;
								mobileMenuOpen = false;
							}}
						>
							Events
						</a>

						<div class="pt-2">
							<p class="px-3 py-1 text-xs font-semibold text-blue-200">Stay Connected</p>
							<a
								href="/newsletter"
								class={cn(
									'flex items-center rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/newsletter'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileCalendarOpen = false;
									mobileMenuOpen = false;
								}}
							>
								<Mail class="mr-2 h-4 w-4" />
								Newsletter
							</a>
							<a
								href="/feeds"
								class={cn(
									'flex items-center rounded-md px-3 py-2 text-sm font-medium',
									$page.url.pathname === '/feeds'
										? 'bg-blue-800 text-white'
										: 'text-blue-100 hover:bg-blue-600'
								)}
								on:click={() => {
									mobileCalendarOpen = false;
									mobileMenuOpen = false;
								}}
							>
								<Rss class="mr-2 h-4 w-4" />
								Subscribe
							</a>
						</div>
					</div>
				{/if}
			</div>

			<a
				href="/search"
				class={cn(
					'flex items-center rounded-md px-3 py-2 text-base font-medium',
					$page.url.pathname === '/search'
						? 'bg-blue-900 dark:bg-blue-800'
						: 'hover:bg-blue-700 dark:hover:bg-blue-800'
				)}
			>
				<Search class="mr-2 h-4 w-4" />
				Search</a
			>

			<!-- Mobile Resources dropdown -->
			<div class="relative" use:clickOutside on:outclick={() => (mobileResourcesOpen = false)}>
				<button
					id="mobile-resources-button"
					class={cn(
						'flex w-full items-center rounded-md px-3 py-2 text-base font-medium',
						$page.url.pathname.startsWith('/resources')
							? 'bg-blue-900 dark:bg-blue-800'
							: 'hover:bg-blue-700 dark:hover:bg-blue-800'
					)}
					on:click={() => (mobileResourcesOpen = !mobileResourcesOpen)}
				>
					<BookOpen class="mr-2 h-4 w-4" />
					Resources
					<span class="transition-transform" class:rotate-180={mobileResourcesOpen}>
						<ChevronDown class="ml-1 h-4 w-4" aria-hidden="true" />
					</span>
				</button>
				{#if mobileResourcesOpen}
					<div
						id="mobile-resources-dropdown"
						class="mt-2 space-y-1 rounded-md bg-blue-700 px-4 py-2"
					>
						<a
							href="/resources/repeaters"
							class={cn(
								'flex items-center rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/resources/repeaters'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileResourcesOpen = false;
								mobileMenuOpen = false;
							}}
						>
							<RadioIcon class="mr-2 h-4 w-4" />
							Repeater List
						</a>
						<a
							href="/resources/testing"
							class={cn(
								'flex items-center rounded-md px-3 py-2 text-sm font-medium',
								$page.url.pathname === '/resources/testing'
									? 'bg-blue-800 text-white'
									: 'text-blue-100 hover:bg-blue-600'
							)}
							on:click={() => {
								mobileResourcesOpen = false;
								mobileMenuOpen = false;
							}}
						>
							<RadioIcon class="mr-2 h-4 w-4" />
							License Testing
						</a>
					</div>
				{/if}
			</div>

			<a
				href="/contact"
				class={cn(
					'flex items-center rounded-md px-3 py-2 text-base font-medium',
					$page.url.pathname === '/contact'
						? 'bg-blue-900 text-white'
						: 'text-blue-100 hover:bg-blue-700'
				)}
			>
				<MessageCircle class="mr-2 h-4 w-4" />
				Contact</a
			>
		</div>
	</div>
{/if}
